import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card, Input, Select, Button, List, Tag, Segmented, DatePicker, Badge, message, Empty, Popconfirm, Modal, Checkbox,
} from 'antd';
import { PlusOutlined, CheckOutlined, UndoOutlined, DeleteOutlined, MessageOutlined, EditOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAgendaItems, createAgendaItem, updateAgendaItem, toggleAgendaItemComplete, deleteAgendaItem } from '../api/agendaItems';
import { getAgendaTags } from '../api/agendaTags';
import useDebounce from '../hooks/useDebounce';
import usePersistedPageSize from '../hooks/usePersistedPageSize';
import { URGENCY_OPTIONS, urgencyMeta } from '../constants/agendaUrgency';
import AgendaNotesModal from '../components/agenda/AgendaNotesModal';
import AgendaTagSelect from '../components/agenda/AgendaTagSelect';

const { TextArea } = Input;
const { Option } = Select;

/**
 * Hatirlatma filtresi TEK kontrol, cunku secenekler BIRBIRIYLE CELISIR:
 * backend'e hem `reminder_date` hem `reminder_date_before` gonderilirse
 * sorgu "tarihi tam X VE X'ten kucuk" olur ve HIC SONUC DONMEZ. Iki ayri
 * kutu birakilsaydi kullanici ikisini birden doldurabilir ve bos listeyi
 * "kayit yok" sanirdi.
 */
const HATIRLATMA_SECENEKLERI = [
  { value: 'all',     label: 'Hatırlatma: Tümü' },
  { value: 'overdue', label: 'Tarihi geçenler' },
  { value: 'today',   label: 'Bugün' },
  { value: 'date',    label: 'Belirli bir tarih…' },
];

const Agenda = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [tags, setTags] = useState([]);
  const [tagsLoaded, setTagsLoaded] = useState(false);

  const [content, setContent] = useState('');
  const [newUrgency, setNewUrgency] = useState('none');
  const [newReminderDate, setNewReminderDate] = useState(null);
  const [newTagId, setNewTagId] = useState(null);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [urgencyFilter, setUrgencyFilter] = useState(null);
  // Panodaki "Ajanda — Hatırlatmalar" kartı buraya BAĞLANTIYLA geliyor
  // (?hatirlatma=overdue&status=pending). Eskiden "Tümünü Gör" filtresiz
  // /agenda açıyordu: kullanıcı panoda "10+ tarihi geçen" görüp tıklıyor,
  // karşısına tüm görevlerin karışık listesi çıkıyor ve gecikenleri
  // ayıramıyordu (Eren Bey bildirdi, 12.10.2026).
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(
    () => (['pending', 'completed'].includes(searchParams.get('status'))
      ? searchParams.get('status')
      : 'all'),
  );
  const [tagFilter, setTagFilter] = useState(undefined);
  const [hatirlatmaFiltre, setHatirlatmaFiltre] = useState(
    () => (HATIRLATMA_SECENEKLERI.some((o) => o.value === searchParams.get('hatirlatma'))
      ? searchParams.get('hatirlatma')
      : 'all'),
  );
  const [reminderDateFilter, setReminderDateFilter] = useState(null);
  const [hasNotesFilter, setHasNotesFilter] = useState(false);

  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('agenda', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });

  const [notesModalItem, setNotesModalItem] = useState(null);

  const [editingItem, setEditingItem] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editUrgency, setEditUrgency] = useState('none');
  const [editReminderDate, setEditReminderDate] = useState(null);
  const [editTagId, setEditTagId] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  /**
   * Secili hatirlatma filtresini backend parametresine cevirir.
   * `reminder_date` ile `reminder_date_before` ASLA birlikte gonderilmez
   * (yukaridaki nota bak).
   */
  const hatirlatmaParametreleri = useCallback(() => {
    const bugun = dayjs().format('YYYY-MM-DD');
    if (hatirlatmaFiltre === 'overdue') return { reminder_date_before: bugun };
    if (hatirlatmaFiltre === 'today')   return { reminder_date: bugun };
    if (hatirlatmaFiltre === 'date' && reminderDateFilter) {
      return { reminder_date: reminderDateFilter.format('YYYY-MM-DD') };
    }
    return {};
  }, [hatirlatmaFiltre, reminderDateFilter]);

  const fetchItems = useCallback(async (page = 1, perPage = pagination.pageSize, tagId = tagFilter) => {
    setLoading(true);
    try {
      const res = await getAgendaItems({
        page,
        per_page: perPage,
        search: debouncedSearch || undefined,
        urgency: urgencyFilter || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        agenda_tag_id: tagId || undefined,
        ...hatirlatmaParametreleri(),
        has_notes: hasNotesFilter ? 1 : undefined,
      });
      setItems(res.data.data);
      setPagination({
        current: res.data.current_page,
        pageSize: res.data.per_page,
        total: res.data.total,
      });
    } catch (error) {
      message.error('Ajanda öğeleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, urgencyFilter, statusFilter, tagFilter, hatirlatmaFiltre, reminderDateFilter, hasNotesFilter]);

  // Tag'ler yukleniyor, filtre varsayilan olarak "Tumu" (undefined) kaliyor -
  // giriste otomatik ilk tag'i secip listeyi daraltmiyoruz.
  useEffect(() => {
    getAgendaTags().then((res) => {
      setTags(res.data.data || []);
      setTagsLoaded(true);
    }).catch(() => setTagsLoaded(true));
  }, []);

  useEffect(() => {
    if (!tagsLoaded) return;
    fetchItems(1, pagination.pageSize, tagFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsLoaded, debouncedSearch, urgencyFilter, statusFilter, tagFilter, hatirlatmaFiltre, reminderDateFilter, hasNotesFilter]);

  const handleCreate = async () => {
    if (!content.trim()) {
      message.warning('Görev içeriği boş olamaz.');
      return;
    }
    setSubmitting(true);
    try {
      await createAgendaItem({
        content: content.trim(),
        urgency: newUrgency,
        reminder_date: newReminderDate ? newReminderDate.format('YYYY-MM-DD') : undefined,
        agenda_tag_id: newTagId || undefined,
      });
      setContent('');
      setNewUrgency('none');
      setNewReminderDate(null);
      setNewTagId(null);
      message.success('Görev eklendi.');
      fetchItems(1, pagination.pageSize);
    } catch (error) {
      message.error('Görev eklenemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleComplete = async (item) => {
    try {
      await toggleAgendaItemComplete(item.id);
      fetchItems(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('İşlem başarısız.');
    }
  };

  const handleDelete = async (item) => {
    try {
      await deleteAgendaItem(item.id);
      message.success('Görev silindi.');
      fetchItems(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('Görev silinemedi.');
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setEditContent(item.content);
    setEditUrgency(item.urgency);
    setEditReminderDate(item.reminder_date ? dayjs(item.reminder_date) : null);
    setEditTagId(item.agenda_tag_id || null);
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim()) {
      message.warning('Görev içeriği boş olamaz.');
      return;
    }
    setSavingEdit(true);
    try {
      await updateAgendaItem(editingItem.id, {
        content: editContent.trim(),
        urgency: editUrgency,
        reminder_date: editReminderDate ? editReminderDate.format('YYYY-MM-DD') : null,
        agenda_tag_id: editTagId || null,
      });
      message.success('Görev güncellendi.');
      setEditingItem(null);
      fetchItems(pagination.current, pagination.pageSize);
    } catch {
      message.error('Görev güncellenemedi.');
    } finally {
      setSavingEdit(false);
    }
  };

  const isReminderDue = (item) => {
    if (!item.reminder_date) return false;
    const d = dayjs(item.reminder_date);
    return d.isBefore(dayjs(), 'day') || d.isSame(dayjs(), 'day');
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Ajanda</h2>

      <Card className="mb-4">
        <TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Yapılacak işi yazın..."
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={5000}
        />
        <div className="flex justify-between items-center mt-3 flex-wrap gap-2">
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={newUrgency} onChange={setNewUrgency} style={{ width: 180 }}>
              {URGENCY_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
            <DatePicker
              value={newReminderDate}
              onChange={setNewReminderDate}
              format="DD.MM.YYYY"
              placeholder="Hatırlatma tarihi (opsiyonel)"
              style={{ width: 200 }}
            />
            <AgendaTagSelect
              value={newTagId}
              onChange={setNewTagId}
              tags={tags}
              setTags={setTags}
              placeholder="Tag yok"
              allowClear
              style={{ width: 180 }}
            />
          </div>
          <Button type="primary" icon={<PlusOutlined />} loading={submitting} onClick={handleCreate}>
            Ekle
          </Button>
        </div>
      </Card>

      <Card>
        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <Input
            placeholder="Görev içinde ara..."
            allowClear
            style={{ width: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            placeholder="Aciliyete göre filtrele"
            allowClear
            style={{ width: 200 }}
            value={urgencyFilter}
            onChange={(v) => setUrgencyFilter(v || null)}
          >
            {URGENCY_OPTIONS.map((o) => (
              <Option key={o.value} value={o.value}>{o.label}</Option>
            ))}
          </Select>
          <AgendaTagSelect
            value={tagFilter}
            onChange={(v) => setTagFilter(v || undefined)}
            tags={tags}
            setTags={setTags}
            placeholder="Tümü"
            allowClear
            style={{ width: 180 }}
          />
          <Select
            value={hatirlatmaFiltre}
            onChange={(v) => {
              setHatirlatmaFiltre(v);
              // Tarih kutusu kapanirken secili tarih de temizlenmeli, yoksa
              // kullanici "Tumu"ne donduğunde eski tarih gizli filtre olarak
              // asili kalir.
              if (v !== 'date') setReminderDateFilter(null);
              // Baglantiyla gelindiyse adres cubugu da takip etsin - sayfa
              // yenilendiginde ayni liste acilsin.
              const q = new URLSearchParams(searchParams);
              if (v === 'all') q.delete('hatirlatma'); else q.set('hatirlatma', v);
              setSearchParams(q, { replace: true });
            }}
            style={{ width: 190 }}
          >
            {HATIRLATMA_SECENEKLERI.map((o) => (
              <Option key={o.value} value={o.value}>{o.label}</Option>
            ))}
          </Select>
          {hatirlatmaFiltre === 'date' && (
            <DatePicker
              value={reminderDateFilter}
              onChange={setReminderDateFilter}
              format="DD.MM.YYYY"
              placeholder="Tarih seçin"
              style={{ width: 170 }}
              allowClear
            />
          )}
          <Segmented
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: 'Tümü', value: 'all' },
              { label: 'Bekleyen', value: 'pending' },
              { label: 'Tamamlanan', value: 'completed' },
            ]}
          />
          <Checkbox checked={hasNotesFilter} onChange={(e) => setHasNotesFilter(e.target.checked)}>
            Notu olanlar
          </Checkbox>
        </div>

        <List
          loading={loading}
          dataSource={items}
          locale={{ emptyText: <Empty description="Görev bulunamadı." /> }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50', '100'],
            onChange: (page, pageSize) => {
              setPersistedPageSize(pageSize);
              fetchItems(page, pageSize);
            },
            onShowSizeChange: (page, pageSize) => {
              setPersistedPageSize(pageSize);
              fetchItems(1, pageSize);
            },
            showTotal: (total) => `Toplam ${total} görev`,
          }}
          renderItem={(item) => {
            const meta = urgencyMeta(item.urgency);
            const reminderDue = isReminderDue(item);
            return (
              <List.Item
                key={item.id}
                style={reminderDue ? { background: '#fff7e6' } : undefined}
                actions={[
                  item.is_completed ? (
                    <Popconfirm
                      title="Tamamlandı işaretini geri almak istiyor musunuz?"
                      onConfirm={() => handleToggleComplete(item)}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Button size="small" icon={<UndoOutlined />}>Geri Al</Button>
                    </Popconfirm>
                  ) : (
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={() => handleToggleComplete(item)}
                    >
                      Tamamlandı
                    </Button>
                  ),
                  <Button key="edit" size="small" icon={<EditOutlined />} onClick={() => startEdit(item)}>
                    Düzenle
                  </Button>,
                  <Badge key="note" count={item.notes_count || 0} size="small" offset={[-4, 4]}>
                    <Button
                      size="small"
                      icon={<MessageOutlined />}
                      onClick={() => setNotesModalItem(item)}
                    >
                      Notlar
                    </Button>
                  </Badge>,
                  <Popconfirm
                    key="delete"
                    title="Bu görevi silmek istediğinize emin misiniz?"
                    onConfirm={() => handleDelete(item)}
                    okText="Evet"
                    cancelText="Hayır"
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>Sil</Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag color={meta.color}>{meta.label}</Tag>
                      {item.tag && <Tag color={item.tag.color || 'purple'}>{item.tag.label}</Tag>}
                      {item.is_completed && <Tag color="green">Tamamlandı</Tag>}
                      {item.reminder_date && (
                        <Tag icon={<ClockCircleOutlined />} color={reminderDue ? 'red' : 'default'}>
                          {dayjs(item.reminder_date).format('DD.MM.YYYY')}
                        </Tag>
                      )}
                      <span className="text-gray-400 text-xs">
                        {dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}
                      </span>
                    </div>
                  }
                  description={
                    <div
                      style={{
                        whiteSpace: 'pre-wrap',
                        textDecoration: item.is_completed ? 'line-through' : 'none',
                        color: item.is_completed ? '#999' : 'inherit',
                      }}
                    >
                      {item.content}
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Card>

      <Modal
        title="Görevi Düzenle"
        open={!!editingItem}
        onCancel={() => setEditingItem(null)}
        onOk={handleSaveEdit}
        confirmLoading={savingEdit}
        okText="Kaydet"
        cancelText="İptal"
      >
        <TextArea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={5000}
          className="mb-3"
        />
        <div className="flex gap-2 items-center flex-wrap mt-3">
          <Select value={editUrgency} onChange={setEditUrgency} style={{ width: 180 }}>
            {URGENCY_OPTIONS.map((o) => (
              <Option key={o.value} value={o.value}>{o.label}</Option>
            ))}
          </Select>
          <DatePicker
            value={editReminderDate}
            onChange={setEditReminderDate}
            format="DD.MM.YYYY"
            placeholder="Hatırlatma tarihi (opsiyonel)"
            style={{ width: 200 }}
            allowClear
          />
          <AgendaTagSelect
            value={editTagId}
            onChange={setEditTagId}
            tags={tags}
            setTags={setTags}
            placeholder="Tag yok"
            allowClear
            style={{ width: 180 }}
          />
        </div>
      </Modal>

      <AgendaNotesModal
        item={notesModalItem}
        open={!!notesModalItem}
        onClose={() => { setNotesModalItem(null); fetchItems(pagination.current, pagination.pageSize); }}
      />
    </div>
  );
};

export default Agenda;
